/*!
 * midiClockGrid.js — the beat grid follows MIDI clock: clock(midi)
 * © 2026 Rob Canning — GPLv3
 *
 * A DAW, a sequencer or Strudel (midicmd("clock*48")) sends MIDI clock: 24
 * ticks a beat, start, continue, stop, song position. With clock(midi) the
 * grid goes with it:
 *
 *   - start: the first tick is beat 0, so a bar of theirs is a bar here
 *     (quant:4 lands on their downbeat);
 *   - running: each whole beat they count is checked against the grid's,
 *     and the grid is set again only when the tempo has moved or the beat
 *     is more than DRIFT_MS out -- ticks jitter by a ms or two, and setting
 *     the grid on every one would pass the jitter on;
 *   - not running (many sources send clock while stopped): the tempo only,
 *     from the next whole beat.
 *
 * In a room this machine is then the room's clock: what it sets is shared
 * (roomBeat.js), so the others follow the MIDI clock too. The timing is the
 * source's: MIDI clock from a browser is often uneven, so try it first.
 *
 * The decision (follow, or not) is the pure part: feedClock(), testable alone.
 */

import { createClockDecoder } from "../../system/timecode.js";
import { setTimeline, gridNow, beatAt, gridBpm, wholeBeatAfter, msAtBeat } from "./beatGrid.js";
import { shareTimeline } from "./roomBeat.js";

const DRIFT_MS = 3;          // a beat further out than this: the grid is set again
const BPM_STEP = 0.2;        // a tempo that has moved more than this, likewise

/**
 * A follower: feed(bytes, at, grid) -> the timeline to set, or null.
 * grid: { msAt(perf), beatAt(ms), bpmAt(ms), wholeBeatAfter(ms), msAtBeat(beat) }
 * (the beat grid, or a stand-in in a test).
 */
export function createClockFollower({ average = 24 } = {}) {
  const decoder = createClockDecoder({ average });
  let started = false;                // a start or continue came: their beats are ours
  // the tempo from the ticks' spacing, running or not (the decoder says
  // nothing of ticks while the source is stopped)
  const gaps = [];
  let lastTick = null, sinceTempo = 0;
  return {
    feed(bytes, at, grid) {
      let tempo = null;
      if (bytes?.[0] === 0xf8) {
        if (lastTick != null && at - lastTick > 0 && at - lastTick < 250) {
          gaps.push(at - lastTick);
          if (gaps.length > average) gaps.shift();
        }
        lastTick = at;
        if (gaps.length >= Math.min(average, 12)) tempo = 60000 / ((gaps.reduce((s, g) => s + g, 0) / gaps.length) * 24);
      }
      const ev = decoder.feed(bytes, at);
      if (ev?.kind === "start" || ev?.kind === "continue") { started = true; return null; }
      if (ev?.kind === "stop") { started = false; return null; }
      if (ev?.kind === "position") return null;
      if (!(tempo > 0)) return null;
      const ms = grid.msAt(at);
      const bpm = Math.round(tempo * 100) / 100;
      if (!started || !ev) {
        // no start heard: their tempo, from our next whole beat -- looked at
        // once a beat (24 ticks), not on every tick
        if (++sinceTempo < 24) return null;
        sinceTempo = 0;
        if (Math.abs(bpm - grid.bpmAt(ms)) <= BPM_STEP) return null;
        const beat = grid.wholeBeatAfter(ms + 50);
        return { ms: grid.msAtBeat(beat), beat, bpm };
      }
      if (ev.kind === "tick" && ev.running) {
        // on their whole beats only: in between a tick's position is a guess
        const whole = Math.abs(ev.beats - Math.round(ev.beats)) < 1e-6;
        if (!whole) return null;
        const outMs = (ev.beats - grid.beatAt(ms)) * 60000 / grid.bpmAt(ms);
        if (Math.abs(outMs) <= DRIFT_MS && Math.abs(bpm - grid.bpmAt(ms)) <= BPM_STEP) return null;
        return { ms, beat: Math.round(ev.beats), bpm };
      }
      return null;
    },
    reset() { decoder.reset(); started = false; gaps.length = 0; lastTick = null; sinceTempo = 0; },
  };
}

const GRID = {
  msAt: perf => gridNow(perf).ms,
  beatAt, bpmAt: ms => gridBpm(ms), wholeBeatAfter, msAtBeat,
};

let follower = null;
let unlisten = null;
const state = { on: false, last: null, sets: 0 };

/** One MIDI system message, from midiIn.js. */
function onSystemMessage(bytes, at) {
  if (!follower) return;
  const tl = follower.feed(bytes, at, GRID);
  if (!tl) return;
  setTimeline(tl, "midi");
  shareTimeline(tl);
  state.last = tl; state.sets++;
}

/**
 * Follow MIDI clock (on), or stop (off). The browser's MIDI inputs are
 * opened (it may ask permission). Resolves { ok, why? }.
 */
export async function followMidiClock(on) {
  if (!on) {
    unlisten?.(); unlisten = null; follower = null; state.on = false;
    return { ok: true };
  }
  const { openWebMidi, onMidiSystem } = await import("../../control/midiIn.js");
  const opened = await openWebMidi();
  if (!opened.ok) return opened;
  follower ??= createClockFollower();
  follower.reset();
  unlisten?.();
  unlisten = onMidiSystem(onSystemMessage);
  state.on = true; state.last = null; state.sets = 0;
  return { ok: true, inputs: opened.count };
}

/** For clock(): following or not, and the last it set. */
export const midiClockStatus = () => ({ ...state });
